import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToggleOptionComponent } from "app/shared/components/form-ui/toggle-option/toggle-option.component";
import { AttachmentConfigDialogComponent } from '../../../company-form-config/modals-config/attachment-config-dialog.component';
import { ValuesModalComponent } from '../../values-dialog/values-dialog.component';
import { lab } from 'chroma-js';
import { UserFieldConfigAttributes } from '@albatrosdeveloper/ave-models-package';
import { fieldTypeOptionsWithouForm } from 'app/shared/constants/lists.constant';
import { ConfirmDialogComponent } from '../../../company-form-config/modals-config/confirm-dialog.component';
import { USER_FIELD_GROUP_OPTIONS, normalizeUserFieldGroupValue } from '../../user-field-group.constants';
import { UF, ufFieldHandlers } from '../../user-field-name.constants';

@Component({
  selector: 'app-user-subfields-config',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule,
    MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTooltipModule, ToggleOptionComponent
  ],
  templateUrl: './user-sub-fields-config.component.html'
})
export class UserSubfieldsConfigComponent implements OnInit {
  form: FormGroup;
  readonly fieldTypeOptions = fieldTypeOptionsWithouForm;
  readonly userFieldGroupOptions = USER_FIELD_GROUP_OPTIONS;
  readonly uf = UF;
  readonly ufField = ufFieldHandlers;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<UserSubfieldsConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { subFields: any[], multiple?: boolean }
  ) {
    this.form = this.fb.group({
      fields: this.fb.array([]),
      multiple: [!!this.data.multiple],
    });
  }

  ngOnInit(): void {
    if (this.data.subFields?.length > 0) {
      this.form.get('multiple')?.disable();
      this.data.subFields.forEach(f => this.addField(f));
    }

    setTimeout(() => this.markFormTouched());
  }

  private markFormTouched(): void {
    this.form.markAllAsTouched();

    this.fields.controls.forEach(control => {
      control.markAllAsTouched();
    });
  }

  get fields(): FormArray { return this.form.get('fields') as FormArray; }

  addField(data: Partial<UserFieldConfigAttributes> = {}): void {
    const fg = this.fb.group({
      // --- CAMPOS NUEVOS ---
      fieldName: [
        data.fieldName || UF.default,
        [Validators.required, Validators.pattern(UF.pattern), this.uniqueFieldNameValidator.bind(this)],
      ],
      fieldLabel: this.fb.group({
        master: [data.fieldLabel?.master || '', Validators.required],
        report: [data.fieldLabel?.report || '', Validators.required],
        mobile: [data.fieldLabel?.mobile || '', Validators.required],
      }),
      fieldType: [data.fieldType || 'text', Validators.required],
      isVisible: [data.isVisible !== false],
      isRequired: [!!data.isRequired],
      isTypeMandatory: [!!data.isTypeMandatory],
      
      // --- DISEÑO Y VALIDACIÓN ---
      position: [data.position || 0, [Validators.required, this.positionInGroupValidator.bind(this)]],
      group: [normalizeUserFieldGroupValue(data.group)],
      groupPosition: [data.groupPosition || 0],
      icon: [data.icon || null],
      iconColor: [data.iconColor || null],
      maxLength: [data.maxLength || null],
      minLength: [data.minLength || null, [this.minLengthRangeValidator.bind(this)]],
      caseConversion: [data.caseConversion ?? 'NONE'],
      trimSpaces: [!!data.trimSpaces],
      mask: [data.mask ?? ''],
      defaultValue: [data.defaultValue || null],
      
      // --- CONFIG EXTENDIDA ---
      allowedExtensions: [data.allowedExtensions || null],
      maxFileSize: [data.maxFileSize || 5],
      multiple: [!!data.multiple, ],
      valuesData: [data.valuesData ?? []],
      parentFieldName: [data.parentFieldName ?? ''],
      schemaMaster: [data.schemaMaster ?? ''],
      attachments: [data.attachments ?? []],

      // --- CAMPOS OBSOLETOS ---
      name: [data.name || ''],
      labels: [data.labels || null],
      dataType: [data.dataType || 'string'],
      required: [data.required || false]
    });

    fg.get('fieldName')?.valueChanges.subscribe(() => this.revalidateFieldName());
  
    fg.get('group')?.valueChanges.subscribe(() => this.revalidateGroupFields());
    fg.get('position')?.valueChanges.subscribe(() => this.revalidateGroupFields());
    fg.get('maxLength')?.valueChanges.subscribe(() => {
      fg.get('minLength')?.updateValueAndValidity({ emitEvent: false });
    });

    fg.markAllAsTouched(); 
    
    this.fields.push(fg);
  }

  minLengthRangeValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;
    const min = control.value;
    const max = formGroup.get('maxLength')?.value;
    if (min === null || min === '' || max === null || max === '') return null;
    return Number(min) > Number(max) ? { minGreaterThanMax: true } : null;
  }

  positionInGroupValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;

    const currentGroup = formGroup.get('group')?.value;
    const currentPos = control.value;

    // Accedemos directamente a this.fields (el FormArray del componente)
    if (!this.fields || currentPos === null || currentPos === '') return null;

    const duplicate = this.fields.controls.find((ctrl) => {
      return ctrl !== formGroup && 
            ctrl.get('group')?.value === currentGroup && 
            ctrl.get('position')?.value == currentPos;
    });

    if (duplicate) {
      const groupPositions = this.fields.controls
        .filter(ctrl => ctrl.get('group')?.value === currentGroup)
        .map(ctrl => Number(ctrl.get('position')?.value) || 0);
      
      return { 
        duplicatePosition: true, 
        lastOccupied: Math.max(...groupPositions) 
      };
    }
    return null;
  }

  // 2. Método para forzar la actualización de validez en el array
  private revalidateGroupFields() {
    if (!this.fields) return;
    this.fields.controls.forEach(control => {
      const fg = control as FormGroup;
      fg.get('position')?.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });
  }

  uniqueFieldNameValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;

    const value = control.value?.trim().toLowerCase();
    if (!value || !this.fields) return null;

    const isDuplicate = this.fields.controls.some((ctrl) => {
      return ctrl !== formGroup && 
            ctrl.get('fieldName')?.value?.trim().toLowerCase() === value;
    });

    return isDuplicate ? { duplicateName: true } : null;
  }

  private revalidateFieldName() {
    if (!this.fields) return;
    this.fields.controls.forEach(control => {
      control.get('fieldName')?.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });
  }

  confirmDeleteField(index: number): void {
    const fieldGroup = this.fields.at(index) as FormGroup;
    const fieldLabel =
      fieldGroup.get('fieldLabel.master')?.value ||
      fieldGroup.get('fieldName')?.value ||
      'este subcampo';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmar eliminación',
        message: `¿Estás seguro de eliminar "${fieldLabel}"?`
      },
      panelClass: 'responsive-modal',
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.fields.removeAt(index);

        this.revalidateFieldName();
        this.revalidateGroupFields();

        this.dialogRef.updateSize(); 
      }
    });
  }

  openConfigModal(fieldGroup: FormGroup) {
    const type = fieldGroup.get('fieldType')?.value;
    
    if (type === 'attachment') {
      this.dialog.open(AttachmentConfigDialogComponent, {
              width: '650px',
              panelClass: 'responsive-modal',
              data: { 
                attachments: fieldGroup.get('attachments').value || [],
                multiple: fieldGroup.get('multiple').value
              }
            }).afterClosed().subscribe(res => {
              if (res) {
                fieldGroup.patchValue({
                  attachments: res.attachments
                });
                fieldGroup.patchValue({
                  multiple: res.multiple
                });
              }
            });
    } else if (['select', 'multiselect'].includes(type)) {
      this.dialog.open(ValuesModalComponent, {
        width: '40%',
        panelClass: 'responsive-modal',
        data: { values: Array.isArray(fieldGroup.get('valuesData')?.value) ? [...fieldGroup.get('valuesData')?.value] : [] }
      }).afterClosed().subscribe(res => res && fieldGroup.get('valuesData')?.setValue(res));
    }
  }

  save(): void {
    if (this.form.valid) {
      const rawFather = this.form.getRawValue();
      const raw = this.form.getRawValue().fields;
      // Mapeo Espejo antes de cerrar
      const processed = raw.map(f => ({
        ...f,
        name: f.fieldName,
        labels: f.fieldLabel,
        dataType: this.convertType(f.fieldType),
        required: f.isRequired,
        position: Number(f.position || 0),
        groupPosition: Number(f.groupPosition || 0),
        maxLength: f.maxLength ? Number(f.maxLength) : null,
        minLength: f.minLength ? Number(f.minLength) : null
      }));
      this.dialogRef.close({ subFields: processed, multiple: rawFather.multiple });
    }
  }

  convertType(type: string): string {
    switch (type) {
      case 'text':
        return 'string';
      case 'select':
        return 'array';
      case 'multiselect':
        return 'object';
      case 'form':
        return 'string';
      case 'attachment':
        return 'string';
      default:
        return type;
    }
  }
}